import { apiUrl } from './client';

/** One of the roles a demo visitor can sign in as. */
export interface DemoLogin {
  key: string;
  /** What the role is called on the sign-in page, e.g. "Engineer". */
  title: string;
  blurb: string;
  /** The person behind it, e.g. "Laila Hassan". */
  name: string;
  role: string;
  email: string;
  password: string;
}

/**
 * A demo site announces itself on the public health endpoint: the roles a
 * visitor can choose between, and when its dataset is next thrown away and
 * rebuilt. Anything else — a normal install — answers without `demoSite`.
 */
export interface DemoSite {
  logins: DemoLogin[];
  /** When the data next resets; null until the server has scheduled it. */
  resetAt: number | null;
  resetMinutes: number;
  /** Identifies the dataset being served. A different value means a reset happened. */
  builtAt: number | null;
}

export async function fetchDemoSite(): Promise<DemoSite | null> {
  try {
    const res = await fetch(apiUrl('/api/health'));
    if (!res.ok) return null;
    const body = (await res.json()) as { demoSite?: DemoSite };
    return body.demoSite?.logins?.length ? body.demoSite : null;
  } catch {
    return null;
  }
}

/**
 * The role this browser last chose. A demo session dies at every reset, and a
 * visitor should come back as who they were rather than be asked again; with
 * nothing stored (a first visit, or after "Sign out") they get the choice.
 */
const ROLE_KEY = 'itr.demo.role';

export const demoRole = {
  get(): string | null {
    try {
      return localStorage.getItem(ROLE_KEY);
    } catch {
      return null;
    }
  },
  set(key: string | null): void {
    try {
      if (key) localStorage.setItem(ROLE_KEY, key);
      else localStorage.removeItem(ROLE_KEY);
    } catch {
      /* storage unavailable — the visitor is simply asked again */
    }
  },
};
